import { useTranslation } from "react-i18next";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import { formatPremiumWithCurrency } from "../../../utils";
import PromoCode from "../../newMotorPolicy/veiwSummaryPopup/promoCode";

const SummaryContent = ({
  planData,
  promoCode,
  setPromoCode,
  applyPromoCode,
  clearPromoCode,
  promoCodeApplied = false,
  promoCodeDiscount = 0,
  isApplyingPromo = false, // Loading state for promo code application
  showQuotationNumber = true,
  showPromoCode = true,
  className = "",
  flowType = null, // New prop to identify insurance flow type
  showNonRefundableNotice = false, // Show non-refundable notice after Total
}) => {
  const translate = useLanguageTranslate();
  const { i18n } = useTranslation();

  if (!planData) return null;

  // Always use English numerals, detect RTL for currency positioning
  const isRTL = i18n.resolvedLanguage === "ar";

  console.log("=== SummaryContent - API Data ===");
  console.log("planData:", planData);
  console.log("basePremium:", planData.basePremium);
  console.log("premium:", planData.premium);
  console.log("tax:", planData.tax);
  console.log("totalPremium:", planData.totalPremium);
  console.log("flowType:", flowType);
  console.log("planData.apiData:", planData.apiData);
  console.log(
    "planData.apiData?.QuoteDetails:",
    planData.apiData?.QuoteDetails
  );
  console.log(
    "planData.apiData?.QuoteDetails?.Covers:",
    planData.apiData?.QuoteDetails?.Covers
  );
  console.log("Direct planData.Covers:", planData.Covers);

  // Use API data directly - NO frontend calculations at all
  // When promo is applied, planData already contains promo-adjusted values from API

  // Store original premium (before any discounts)
  const originalBasePremium = planData.basePremium || planData.premium || 0;

  // When promo is applied, show the discounted premium from promo API response
  // Otherwise show the original premium
  const displayPremium = promoCodeApplied
    ? planData.premium // This is the discounted premium from promo API
    : originalBasePremium;

  const finalTax = planData.tax || 0; // From API (promo API if promo applied)
  const finalTotal = planData.totalPremium || 0; // From API (promo API if promo applied)

  // Use real quotation number from API
  const quotationNumber =
    planData.quoteNumber || planData.policyNumber || "N/A";

  // Helper function for RTL-aware currency formatting - always use English numerals
  const formatCurrency = (amount) => {
    if (amount === "N/A" || amount === null || amount === undefined) return "N/A";
    const currency = translate("BHD", "د.ب");
    const formattedNumber = formatPremiumWithCurrency(amount, "", "en-US");

    // For RTL, put number first then currency (66.000 د.ب)
    if (isRTL) {
      return (
        <span className="flex items-center gap-1 flex-row-reverse">
          <span>{currency}</span>
          <span>{formattedNumber}</span>
        </span>
      );
    }
    // For LTR, currency before number
    return `${currency} ${formattedNumber}`;
  };

  return (
    <div className={`bg-white rounded-lg ${className}`}>
      {/* Promo Code Section */}
      {showPromoCode && (
        <PromoCode
          promoCode={promoCode}
          setPromoCode={setPromoCode}
          applyPromoCode={applyPromoCode}
          clearPromoCode={clearPromoCode}
          promoCodeApplied={promoCodeApplied}
          isLoading={isApplyingPromo}
        />
      )}

      {/* Summary Details */}
      <div className="rounded-lg summary-content bg-white p-4 border border-[#DDE2E4] border-solid">
        {/* Quotation Number */}
        {showQuotationNumber && (
          <div className="flex items-center justify-between bg-[#E8F4D8] px-4 py-2.5 rounded-md mb-2">
            <span className="text-[#5B6871] text-xs not-italic font-medium leading-6">
              {translate("Quotation Number", "رقم التسعيرة")}
            </span>
            <span className="text-[#5B6871] text-xs not-italic font-medium leading-6">
              {quotationNumber}
            </span>
          </div>
        )}

        {/* Premium for all flows */}
        <div className="flex justify-between mb-2 px-0 py-2.5">
          <span className="text-[#1A2024] text-sm not-italic font-normal rtl:font-medium leading-6">
            {flowType === "personalAccidentRenewal"
              ? translate("Premium", "المبلغ")
              : flowType === "domesticHelperRenewal"
              ? translate("Premium", "المبلغ")
              : flowType === "travelRenewal"
              ? translate("Travel Insurance", "تأمين السفر")
              : flowType === "personalCyberRenewal"
              ? translate("Premium", "المبلغ")
              : flowType === "homeRenewal"
              ? translate("Premium", "المبلغ")
              : translate("Premium", "المبلغ")}
          </span>
          <span className="text-[#1A2024] text-sm not-italic font-normal rtl:font-medium leading-6">
            {formatCurrency(originalBasePremium)}
          </span>
        </div>

        {/* Add-on line items for Home Renewal */}
        {flowType === "homeRenewal" &&
          planData.addonItems &&
          planData.addonItems.length > 0 &&
          planData.addonItems.map((item, index) => (
            <div key={index} className="flex justify-between mb-2 px-0 py-2.5">
              <span className="text-[#1A2024] text-sm not-italic font-normal rtl:font-medium leading-6">
                {item.name}
              </span>
              <span className="text-[#1A2024] text-sm not-italic font-normal rtl:font-medium leading-6">
                {formatCurrency(item.price)}
              </span>
            </div>
          ))}

        {/* Coverage Add-ons - Show covers with prices for Travel Add More Covers endorsement */}
        {flowType === "travelAddCovers" &&
          planData.Covers &&
          planData.Covers.length > 0 && (
            <>
              {planData.Covers.map((cover, index) => {
                console.log(`Processing travel add covers cover ${index}:`, cover);
                // Check for Winter Sport coverage (CPER17)
                if ((cover.coverCode || cover.CoverCode) === "CPER17") {
                  return (
                    <div
                      key={index}
                      className="flex justify-between mb-2 px-0 py-2.5"
                    >
                      <span className="text-[#1A2024] text-sm not-italic font-normal rtl:font-medium leading-6">
                        {translate("Winter Activities", "الأنشطة الشتوية")}
                      </span>
                      <span className="text-[#1A2024] text-sm not-italic font-normal rtl:font-medium leading-6">
                        {formatCurrency(cover.Price || 0)}
                      </span>
                    </div>
                  );
                }
                // Check for Terrorism Extension coverage (CPER137)
                if ((cover.coverCode || cover.CoverCode) === "CPER137") {
                  return (
                    <div
                      key={index}
                      className="flex justify-between mb-2 px-0 py-2.5"
                    >
                      <span className="text-[#1A2024] text-sm not-italic font-normal rtl:font-medium leading-6">
                        {translate("Summer Activities", "الأنشطة الصيفية")}
                      </span>
                      <span className="text-[#1A2024] text-sm not-italic font-normal rtl:font-medium leading-6">
                        {formatCurrency(cover.Price || 0)}
                      </span>
                    </div>
                  );
                }
                return null;
              })}
            </>
          )}

        {/* Coverage Add-ons - Show covers with prices from API for Travel */}
        {flowType === "travel" &&
          (() => {
            // Get covers from the correct API response path
            const covers =
              planData.covers ||
              planData.apiData?.QuoteDetails?.Covers ||
              planData.Covers ||
              [];
            console.log("=== SummaryContent - Covers Check ===");
            console.log("Found covers:", covers);
            console.log("Covers length:", covers.length);

            // Filter only the selected add-on covers (CPER17 and CPER137)
            const selectedCovers = covers.filter(
              (cover) =>
                (cover.coverCode || cover.CoverCode) === "CPER17" || (cover.coverCode || cover.CoverCode) === "CPER137"
            );
            console.log("Selected covers:", selectedCovers);
            return selectedCovers.length > 0;
          })() && (
            <>
              {(() => {
                const covers =
                  planData.covers ||
                  planData.apiData?.QuoteDetails?.Covers ||
                  planData.Covers ||
                  [];
                const selectedCovers = covers
                  .filter(
                    (cover) =>
                      (cover.coverCode || cover.CoverCode) === "CPER17" ||
                      (cover.coverCode || cover.CoverCode) === "CPER137"
                  )
                  .sort((a, b) => {
                    // Sort order: Winter & Water Sports (CPER17) first, then Terrorism (CPER137)
                    const orderMap = { CPER17: 1, CPER137: 2 };
                    const codeA = a.coverCode || a.CoverCode;
                    const codeB = b.coverCode || b.CoverCode;
                    return (orderMap[codeA] || 999) - (orderMap[codeB] || 999);
                  });

                return selectedCovers.map((cover, index) => {
                  console.log(`Processing selected cover ${index}:`, cover);
                  // Check for Winter Sport coverage
                  if ((cover.coverCode || cover.CoverCode) === "CPER17") {
                    return (
                      <div
                        key={index}
                        className="flex justify-between mb-2 px-0 py-2.5"
                      >
                        <span className="text-[#1A2024] text-sm not-italic font-normal rtl:font-medium leading-6">
                          {translate("Winter Activities", "الأنشطة الشتوية")}
                        </span>
                        <span className="text-[#1A2024] text-sm not-italic font-normal rtl:font-medium leading-6">
                          {formatCurrency(
                            planData.getCoverPriceByCode
                              ? planData.getCoverPriceByCode("CPER17")
                              : cover.Price || 0
                          )}
                        </span>
                      </div>
                    );
                  }
                  // Check for Terrorism Extension coverage
                  if ((cover.coverCode || cover.CoverCode) === "CPER137") {
                    return (
                      <div
                        key={index}
                        className="flex justify-between mb-2 px-0 py-2.5"
                      >
                        <span className="text-[#1A2024] text-sm not-italic font-normal rtl:font-medium leading-6">
                          {translate("Summer Activities", "الأنشطة الصيفية")}
                        </span>
                        <span className="text-[#1A2024] text-sm not-italic font-normal rtl:font-medium leading-6">
                          {formatCurrency(
                            planData.getCoverPriceByCode
                              ? planData.getCoverPriceByCode("CPER137")
                              : cover.Price || 0
                          )}
                        </span>
                      </div>
                    );
                  }
                  return null;
                });
              })()}
            </>
          )}

        {/* Coverage Add-ons - Show only selected covers from cover-prices API for Home */}
        {flowType === "home" &&
          (() => {
            // Get coverPrices from form data and check for selected covers
            const coverPrices = planData.coverPrices || [];

            console.log("=== SummaryContent - Home Covers Check ===");
            console.log("Cover prices:", coverPrices);
            console.log("Coverages data:", planData.coverages);

            // Check if any covers are selected in form data
            const hasSelectedCovers = coverPrices.some((cover) => {
              const coverKey = cover.CoverCode.toLowerCase();
              return (
                planData.coverages &&
                (planData.coverages[coverKey] ||
                  // Legacy support for old keys
                  (cover.CoverCode === "CPRO21" &&
                    planData.coverages.allRisk) ||
                  (cover.CoverCode === "CPRO13" &&
                    planData.coverages.vipCover) ||
                  (cover.CoverCode === "CPRO35" &&
                    planData.coverages.domesticHelper) ||
                  (cover.CoverCode === "CPRO33" &&
                    planData.coverages.sabotageAndTerrorism))
              );
            });

            console.log("Has selected covers:", hasSelectedCovers);
            return hasSelectedCovers;
          })() && (
            <>
              {(() => {
                const coverPrices = planData.coverPrices || [];

                return coverPrices
                  .map((cover) => {
                    const coverKey = cover.CoverCode.toLowerCase();

                    // Check if this cover is actually selected by the user (use form data, not API data)
                    const isSelected =
                      planData.coverages &&
                      (planData.coverages[coverKey] ||
                        // Legacy support for old keys
                        (cover.CoverCode === "CPRO21" &&
                          planData.coverages.allRisk) ||
                        (cover.CoverCode === "CPRO13" &&
                          planData.coverages.vipCover) ||
                        (cover.CoverCode === "CPRO35" &&
                          planData.coverages.domesticHelper) ||
                        (cover.CoverCode === "CPRO33" &&
                          planData.coverages.sabotageAndTerrorism));

                    // Only show selected covers (not basic covers)
                    if (isSelected) {
                      console.log(`Processing selected home cover:`, cover);

                      const coverPrice = cover.Price || 0;

                      console.log(
                        `💰 Home Summary Selected Cover ${cover.CoverCode}: Cover-Prices API Price=${coverPrice}`
                      );

                      // Translate cover names for Arabic
                      const homeCoverTranslations = {
                        "All Risk": translate("All Risk", "جميع المخاطر"),
                        "VIP": translate("VIP", "تغطية كبار الشخصيات"),
                        "Domestic Helper": translate("Domestic Helper", "العامل المنزلي"),
                        "Terrorism": translate("Terrorism", "تغطية التخريب والإرهاب"),
                        "Sabotage and Terrorism": translate("Sabotage and Terrorism", "تغطية التخريب والإرهاب"),
                      };
                      const displayName = homeCoverTranslations[cover.CoverName] || cover.CoverName;

                      return (
                        <div
                          key={cover.CoverCode}
                          className="flex justify-between mb-2 px-0 py-2.5"
                        >
                          <span className="text-[#1A2024] text-sm not-italic font-normal rtl:font-medium leading-6">
                            {displayName}
                          </span>
                          <span className="text-[#1A2024] text-sm not-italic font-normal rtl:font-medium leading-6">
                            {formatCurrency(coverPrice)}
                          </span>
                        </div>
                      );
                    }
                    return null;
                  })
                  .filter(Boolean); // Remove null values
              })()}
            </>
          )}

        {/* Discount */}
        {promoCodeApplied && (
          <div className="flex justify-between mb-2 px-0 py-2.5 border-t-[#DDE2E4] border-t border-solid">
            <span className="text-[#1A2024] text-sm not-italic font-normal rtl:font-medium leading-6">
              {translate(`Discount (${promoCode})`, `خصم (${promoCode})`)}
            </span>
            <span className="text-[#F2271C] text-sm not-italic font-normal leading-6">
              - {formatCurrency(promoCodeDiscount)}
            </span>
          </div>
        )}

        {/* VAT */}
        <div className="flex justify-between mb-2 px-0 py-2.5 border-t-[#DDE2E4] border-t border-solid">
          <span className="text-[#1A2024] text-sm not-italic font-normal rtl:font-medium leading-6">
            {translate("VAT", "ضريبة القيمة المضافة")} 10%
          </span>
          <span className="text-[#1A2024] text-sm not-italic font-normal rtl:font-medium leading-6">
            {formatCurrency(finalTax)}
          </span>
        </div>

        {/* Total */}
        <div className="flex justify-between px-0 py-2.5 border-t-[#DDE2E4] border-t border-solid">
          <span className="text-[#1A2024] text-base not-italic font-extrabold rtl:font-bold leading-6">
            {translate("Total", "المجموع")}
          </span>
          <span className="text-[#1A2024] text-base not-italic font-extrabold leading-6">
            {formatCurrency(finalTotal)}
          </span>
        </div>

        {/* Non-refundable Notice */}
        {showNonRefundableNotice && (
          <div className="pt-2.5">
            <p className="text-sm text-[#5b6871] text-center leading-6">
              {translate(
                "This policy is non-refundable in case of cancellation for any reason",
                "هذه الوثيقة غير قابلة للاسترداد في حالة الإلغاء لأي سبب"
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SummaryContent;
